import React, { FC, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useDepartments } from 'context/departments-context';
import DepartmentWorkflowSteps from './department-workflow-steps/department-workflow-steps.component';
import DepartmentTechnicians from './department-technicians/department-technicians.component';
import { Department, DepartmentWorkflowStep } from 'models/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { User } from 'models/user';
import TimeInput from 'components/shared/time-input';

type AddEditDepartmentDialogProps = {
    open: boolean;
    department?: Department;
    onDialogClose: () => void;
};
const AddEditDepartmentDialog: FC<AddEditDepartmentDialogProps> = ({ open, department, onDialogClose }) => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow', 'common']);
    const [name, setName] = useState(department?.name || '');
    const [steps, setSteps] = useState<DepartmentWorkflowStep[]>([]);
    const [technicians, setTechnicians] = useState<string[]>([]);
    const [productionHourLimit, setProductionHourLimit] = useState<number | undefined>(undefined);
    const [capacity, setCapacity] = useState<number | undefined>(undefined);
    const [isTimeInputValid, setIsTimeInputValid] = useState(true);
    const { saveDepartment, updateDepartment } = useDepartments();
    const { setAppToasterContent } = useContext(AppToasterContext);

    useEffect(() => {
        if (department?._id) {
            setName(department.name || '');
            const techniciansIds = (department.technicians as User[])?.map((tech) => tech._id) || [];
            setTechnicians(techniciansIds);
            setSteps(department.workflowSteps || []);
            setProductionHourLimit(department.productionHourLimit);
            setCapacity(department.capacity);
        } else {
            setName('');
            setSteps([]);
            setTechnicians([]);
            setProductionHourLimit(undefined);
            setCapacity(undefined);
        }
    }, [department, open]);

    const handleStepsChange = (steps: DepartmentWorkflowStep[]) => {
        setSteps(steps);
    };

    const handleTechniciansChange = (technicians: string[]) => {
        setTechnicians(technicians);
    };

    const isAddButtonDisabled = !name.trim() || steps?.length === 0 || !isTimeInputValid || !capacity || capacity < 1;

    const handleSubmit = async () => {
        const departmentData = {
            _id: department?._id,
            name,
            technicians,
            workflowSteps: steps.map((step) => ({
                stepId: step._id,
                stepOrder: step.stepOrder,
            })),
            productionHourLimit: productionHourLimit || undefined,
            capacity: capacity ?? undefined,
        };

        try {
            if (department?._id) {
                await updateDepartment({
                    department: {
                        ...departmentData,
                    },
                });

                setAppToasterContent({
                    isOpen: true,
                    severity: 'success',
                    message: translate('common:CHANGES_SAVED'),
                });
            } else {
                await saveDepartment({ department: departmentData });

                setAppToasterContent({
                    isOpen: true,
                    severity: 'success',
                    message: translate('lab-settings:DEPARTMENT_ADDED_SUCCESSFULLY'),
                });
            }

            onDialogClose();
        } catch (error) {
            console.error('Error saving production template:', error);
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('common:ERROR_OCCURRED'),
            });
        }
    };

    return (
        <DHDialog
            title={
                department?._id ? translate('lab-settings:EDIT_DEPARTMENT') : translate('lab-settings:ADD_DEPARTMENT')
            }
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={onDialogClose}
            height="450px"
            actions={
                <Button variant="contained" type="submit" onClick={handleSubmit} disabled={isAddButtonDisabled}>
                    {department?._id ? translate('common:UPDATE') : translate('common:ADD')}
                </Button>
            }
        >
            <Box mt={1}>
                <Grid container spacing={2}>
                    <Grid item sm={7}>
                        <TextField
                            fullWidth
                            label={translate('lab-settings:DEPARTMENT_NAME')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            variant="outlined"
                            size="small"
                            data-test="department-name-input"
                            required
                        />
                    </Grid>
                    <Grid item sm={3}>
                        <TimeInput
                            fullWidth
                            label={translate('lab-settings:PRODUCTION_HOUR_LIMIT')}
                            value={productionHourLimit}
                            onChange={setProductionHourLimit}
                            onValidChange={setIsTimeInputValid}
                            variant="outlined"
                            size="small"
                            data-test="production-hour-limit-input"
                        />
                    </Grid>
                    <Grid item sm={2}>
                        <TextField
                            fullWidth
                            type="number"
                            label={translate('lab-settings:CAPACITY')}
                            value={capacity ?? ''}
                            onChange={(e) => setCapacity(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                            variant="outlined"
                            size="small"
                            inputProps={{ min: 1 }}
                            required
                            data-test="capacity-input"
                        />
                    </Grid>
                    <Grid item sm={7}>
                        <Box mb={1}>
                            <Typography variant="h4">{translate('lab-settings:WORKFLOW_STEPS')}</Typography>
                        </Box>
                        <DepartmentWorkflowSteps department={department} onStepsChange={handleStepsChange} />
                    </Grid>
                    <Grid item sm={5}>
                        <Box mb={1}>
                            <Typography variant="h4">{translate('common:TECHNICIANS')}</Typography>
                        </Box>
                        <DepartmentTechnicians department={department} onTechniciansChange={handleTechniciansChange} />
                    </Grid>
                </Grid>
            </Box>
        </DHDialog>
    );
};

export default AddEditDepartmentDialog;
